/**
 * M7 — FS Quinzenal (Fator de Segurança).
 *
 * Réplica frontend-only do módulo M7 do protótipo V5 (docs/reference/
 * GeoGuard_UPM_v5_4_4.html, renderM7). Histórico de FS por seção em
 * condição drenada + tabela das últimas leituras com status.
 *
 * POC de visualização para reunião — dados mockados inline, sem
 * datasource/Supabase ainda. Quando o slot integrar (JFB + EoR Walm,
 * formato padrão de upload de FS), trocar MOCK_FS por um DataSource.
 */
import { useMemo } from 'react';
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Card, CardTitle } from '@/ui';

interface FsReading {
  date: string;
  sec: string;
  cond: 'Drenada' | 'Não Drenada';
  fs: number;
}

/** Limiar mínimo de FS — NBR 13028 / ANM Res. 95/220. */
function minFs(cond: FsReading['cond']): number {
  return cond === 'Drenada' ? 1.5 : 1.3;
}

/** Margem acima do mínimo > 10% = OK; senão margem crítica. */
function isOk(r: FsReading): boolean {
  return ((r.fs - minFs(r.cond)) / minFs(r.cond)) * 100 > 10;
}

// Dados do protótipo V5 (FS_DATA) — 16/12/2024 a 07/04/2025.
const MOCK_FS: FsReading[] = [
  { date: '16/12/2024', sec: "B-B'", cond: 'Drenada', fs: 1.82 },
  { date: '16/12/2024', sec: "C-C'", cond: 'Drenada', fs: 1.78 },
  { date: '16/12/2024', sec: "D-D'", cond: 'Drenada', fs: 1.93 },
  { date: '30/12/2024', sec: "B-B'", cond: 'Drenada', fs: 1.8 },
  { date: '30/12/2024', sec: "C-C'", cond: 'Drenada', fs: 1.76 },
  { date: '13/01/2025', sec: "B-B'", cond: 'Drenada', fs: 1.79 },
  { date: '13/01/2025', sec: "C-C'", cond: 'Drenada', fs: 1.74 },
  { date: '27/01/2025', sec: "B-B'", cond: 'Não Drenada', fs: 1.45 },
  { date: '27/01/2025', sec: "C-C'", cond: 'Não Drenada', fs: 1.41 },
  { date: '10/02/2025', sec: "B-B'", cond: 'Drenada', fs: 1.77 },
  { date: '10/02/2025', sec: "C-C'", cond: 'Drenada', fs: 1.73 },
  { date: '24/02/2025', sec: "D-D'", cond: 'Drenada', fs: 1.91 },
  { date: '10/03/2025', sec: "B-B'", cond: 'Drenada', fs: 1.75 },
  { date: '24/03/2025', sec: "C-C'", cond: 'Drenada', fs: 1.71 },
  { date: '07/04/2025', sec: "B-B'", cond: 'Drenada', fs: 1.78 },
];

const SECTIONS = [
  { sec: "B-B'", color: '#FECB00' },
  { sec: "C-C'", color: '#3b82f6' },
  { sec: "D-D'", color: '#22c55e' },
] as const;

export default function M7FsQuinzenal() {
  // Série do gráfico: uma linha por seção (só condição drenada), como no protótipo.
  const { chartData, dates } = useMemo(() => {
    const drained = MOCK_FS.filter((r) => r.cond === 'Drenada');
    const allDates = [...new Set(drained.map((r) => r.date))];
    const rows = allDates.map((date) => {
      const point: Record<string, string | number | null> = { date };
      for (const { sec } of SECTIONS) {
        const r = drained.find((f) => f.date === date && f.sec === sec);
        point[sec] = r ? r.fs : null;
      }
      return point;
    });
    return { chartData: rows, dates: allDates };
  }, []);

  // Tabela: últimas 8 leituras, mais recentes primeiro.
  const lastReadings = useMemo(() => MOCK_FS.slice(-8).reverse(), []);

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-2xl font-black text-jf-yellow">🔢 Fator de Segurança Quinzenal</h1>
        <p className="text-jf-muted text-sm mt-1">
          FS por seção × limites NBR 13028 (1,50 drenado · 1,30 não drenado)
        </p>
      </header>

      <Card className="mb-3">
        <CardTitle>Histórico de FS por seção (condição drenada)</CardTitle>
        <div style={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 12, right: 18, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#1a2c4a" />
              <XAxis
                dataKey="date"
                stroke="#94a3b8"
                tick={{ fontSize: 10 }}
                angle={-45}
                textAnchor="end"
                height={56}
                interval={0}
              />
              <YAxis
                stroke="#94a3b8"
                tick={{ fontSize: 11 }}
                width={44}
                domain={[1.0, 2.3]}
              />
              <Tooltip
                contentStyle={{ background: '#1a2540', border: '1px solid #28334A', borderRadius: 8 }}
                labelStyle={{ color: '#94a3b8' }}
                itemStyle={{ color: '#fff' }}
              />
              <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
              {SECTIONS.map(({ sec, color }) => (
                <Line
                  key={sec}
                  type="monotone"
                  dataKey={sec}
                  name={sec}
                  stroke={color}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  connectNulls
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        {/* dates só serve pra garantir que o eixo X cobre todas as quinzenas */}
        <p className="sr-only">{dates.length} quinzenas</p>
      </Card>

      <Card>
        <CardTitle>Últimas leituras</CardTitle>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-jf-dim text-xs uppercase tracking-wide text-left">
                <th className="py-2 pr-3 font-semibold">Data</th>
                <th className="py-2 pr-3 font-semibold">Seção</th>
                <th className="py-2 pr-3 font-semibold">Condição</th>
                <th className="py-2 pr-3 font-semibold">FS</th>
                <th className="py-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {lastReadings.map((r, i) => {
                const ok = isOk(r);
                return (
                  <tr key={`${r.date}-${r.sec}-${i}`} className="border-t border-jf-border">
                    <td className="py-2 pr-3 font-mono text-jf-muted">{r.date}</td>
                    <td className="py-2 pr-3 font-bold text-white">{r.sec}</td>
                    <td className="py-2 pr-3 text-jf-muted">{r.cond}</td>
                    <td className={`py-2 pr-3 font-mono font-bold ${ok ? 'text-level-normal' : 'text-level-alerta'}`}>
                      {r.fs.toFixed(2)}
                    </td>
                    <td className="py-2">
                      {ok ? (
                        <span className="inline-block rounded-md border border-level-normal/30 bg-level-normal/10 px-2 py-0.5 text-xs font-bold text-level-normal">
                          ✓ OK
                        </span>
                      ) : (
                        <span className="inline-block rounded-md border border-level-alerta/30 bg-level-alerta/10 px-2 py-0.5 text-xs font-bold text-level-alerta">
                          ⚠️ Margem &lt;10%
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
